
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/hooks/useAuth';
import { AuthErrorDisplay } from '../features/auth/components/AuthErrorDisplay';

export const LoginPage = () => {
  const { isAuthenticated, loginWithRedirect, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const [loginWithoutOrg, setLoginWithoutOrg] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = (skipOrganization = false) => {
    const loginOptions: { organization?: string | null; prompt?: string } = {
      prompt: 'login',
    };

    if (skipOrganization) {
      loginOptions.organization = null;
      setLoginWithoutOrg(true);
    }

    loginWithRedirect(loginOptions);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  const isOrgConnectionError = error?.message.includes('no connections enabled for the organization');

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <img 
            src="/PhytoBot_Original.png" 
            alt="Phytobiotics Logo" 
            className="mx-auto h-12 mb-4" 
          />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to PhytoBot
          </h2>
          <p className="text-gray-600">
            Sign in to continue
          </p>
        </div>

        {error && <AuthErrorDisplay error={error} />}

        <div className="space-y-4">
          <button
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            onClick={() => handleLogin()}
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign in with Auth0'}
          </button>

          {isOrgConnectionError && !loginWithoutOrg && (
            <button
              className="w-full px-6 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-sm"
              onClick={() => handleLogin(true)}
            >
              Try without organization
            </button>
          )}
        </div>

        {isOrgConnectionError && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
            <h4 className="font-semibold text-blue-800 mb-2">
              Are you an administrator?
            </h4>
            <p className="text-blue-700">
              To resolve this issue, open the Auth0 Dashboard → Organizations → 
              Connections and enable at least one authentication method (Google, Database, etc.).
            </p>
          </div>
        )}

        <div className="text-center pt-4 border-t">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-semibold text-amber-800 mb-2 text-sm">
              🔒 Restricted Access
            </h4>
            <p className="text-amber-700 text-sm mb-2">
            Accounts are managed by the system administrator.
            </p>
            <div className="text-xs text-amber-600">
              <p className="mb-1">• If you already have an account, sign in above</p>
              <p>• If you need access, contact the administrator</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
